import { Box, Stack } from "@mui/material";
import { useTranslation } from "next-i18next";
import { ArrowLink } from "src/components-mui/base/arrow-link/arrow-link";
import { Typography } from "src/components-mui/base/typography/typography";
import { PopoverButtonIconInfo } from "src/components-mui/base/wf-popover/popover-button-icon-info";
import { WfPopover } from "src/components-mui/base/wf-popover/wf-popover";
import { UnderlyingFollowButton } from "src/components-mui/common/buttons/underlying-follow-button/underlying-follow-button";
import { IUnderlyingListItem } from "src/components-mui/modules/underlying-lists-module/underlying-lists-module.types";
import { getGlobals } from "src/constants/globals";
import { useNumberFormatter } from "src/hooks/formatters/use-number-formatter";
import { getPerformanceColor } from "src/utils/color-util";
import { NO_VALUE_PLACEHOLDER, formatNullable } from "src/utils/format/format-nullable/format-nullable";

// Keep the popover below the fixed header menu (the default "tooltip" z-index would overlay it).
const POPOVER_POPPER_SX = { zIndex: 1 } as const;
const VALUE_SX = { m: 0, fontSize: "inherit", textAlign: "right" } as const;

interface IListRowDetailsProps {
    item: IUnderlyingListItem;
    daysCount: number;
}

export const ListRowDetails = ({ item, daysCount }: IListRowDetailsProps) => {
    const { t } = useTranslation(["underlying-overview", "common"]);
    const { formatNumber, formatNumberWithSign } = useNumberFormatter();
    const formattedPrice = formatNullable({ value: item.bid, format: bid => formatNumber(bid, 2) });
    const formattedPopularityChange = item.popularityChange ? formatNumberWithSign(item.popularityChange) : NO_VALUE_PLACEHOLDER;
    const popularityChangeColor = item.popularityChange ? getPerformanceColor(formattedPopularityChange) : undefined;
    const popularityTooltip = t("popularity.tooltip", { 0: daysCount });

    return (
        <Stack sx={{ gap: 3 }}>
            <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "1fr auto", alignItems: "center", rowGap: 2, columnGap: 2, m: 0 }}>
                <Typography component="dt" sx={{ fontSize: "inherit" }}>
                    {t("price-in-currency", { 0: getGlobals().currency, ns: "common" })}
                </Typography>
                <Typography component="dd" sx={VALUE_SX}>
                    {formattedPrice}
                </Typography>
                <Stack component="dt" direction="row" sx={{ alignItems: "center", gap: 1 }}>
                    <Typography component="span" sx={{ fontSize: "inherit" }}>
                        {t("popularity.label")}
                    </Typography>
                    <WfPopover
                        content={popularityTooltip}
                        PopoverTrigger={<PopoverButtonIconInfo ariaLabel={popularityTooltip} />}
                        slotProps={{ popper: POPOVER_POPPER_SX }}
                    />
                </Stack>
                <Typography component="dd" sx={{ ...VALUE_SX, color: popularityChangeColor }}>
                    {formattedPopularityChange}
                </Typography>
            </Box>
            <Stack
                direction={{ xs: "column", sm: "row", md: "column", lg: "row" }}
                sx={{
                    justifyContent: "space-between",
                    alignItems: { xs: "flex-start", sm: "center", md: "flex-start", lg: "center" },
                    gap: { xs: 2, sm: 1, md: 2, lg: 1 },
                }}
            >
                <UnderlyingFollowButton underlyingIsin={item.isin} />
                <ArrowLink href={item.underlyingDetailsUrl} sx={{ fontSize: "inherit", fontWeight: "bold" }}>
                    {t("stocks.details")}
                </ArrowLink>
            </Stack>
        </Stack>
    );
};
